import { i18n } from '@lingui/core';
import { msg, plural, t } from '@lingui/core/macro';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { resolvePatternText } from '@/shared/patterns/text';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { warrantyExecutionFilters } from './warranty-execution.filters';

// Manifesto da Execução de garantias (U23/U42). Arquivo leve. Só leitura: a exportação baixa um
// arquivo com os filtros da tela e não muda dado (`WarrantyExecutionListHeader.tsx:94-114`).

export const WARRANTY_EXECUTION_PATH = '/gestao-carteira/execucao-garantias';

/** `NaturalPerson:AuctionQuery` **estrito** na rota e nas consultas (`router.tsx:1455-1456`, `warrantyExecutionContext.tsx:81,182`). */
export const AUCTION_QUERY = can('NaturalPerson', 'AuctionQuery', { strict: true });
/** Herdada do grupo Gestão de carteira (`router.tsx:1342-1347`): o guarda do legado confere as duas. */
export const WALLET_READ = can('BillingAccount', 'Read');

export const warrantyExecutionRefresh = action({
  id: 'dataprevEmployee.warrantyExecution.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de execuções de garantia, sem mudar nada.`,
  permission: AUCTION_QUERY,
  run: () => undefined,
});

/** "Exportar Execuções": baixa a planilha do recorte da tela. A tela liga o `run` ao andamento e ao cancelar. */
export const warrantyExecutionExport = action({
  id: 'dataprevEmployee.warrantyExecution.export',
  kind: 'read',
  label: msg`Exportar Execuções`,
  description: msg`Baixa a planilha (xlsx) das execuções de garantia com os mesmos filtros da tela, sem paginação. Não muda nada.`,
  permission: AUCTION_QUERY,
  run: () => undefined,
});

/** Cancelar a exportação em andamento (novo: o legado não tinha). Não muda dado. */
export const warrantyExecutionCancelExport = action({
  id: 'dataprevEmployee.warrantyExecution.cancelExport',
  kind: 'read',
  label: msg`Cancelar exportação`,
  description: msg`Cancela a exportação da planilha em andamento; nada é baixado e nada muda.`,
  permission: AUCTION_QUERY,
  run: () => undefined,
});

export const warrantyExecutionFilter = filterAction('dataprevEmployee.warrantyExecution.filter', warrantyExecutionFilters, {
  description: msg`Filtra as execuções de garantia: executionStatus (0 não executado, 1 executado, 2 recusado, 3 erro), isOwnFund (true carteira própria UY3, false fundos de terceiros), fundExecutionRequested (true/false, solicitado pelo fundo), creditNoteNo (número do contrato, só dígitos), o período de atualização startDate/endDate e o período de execução executedFrom/executedTo (AAAA-MM-DD). Sem filtro, a lista mostra os últimos 90 dias. Filtro omitido é limpo.`,
});

export const warrantyExecutionPage = listPageAction('dataprevEmployee.warrantyExecution.page');

export const warrantyExecutionScreen = defineScreen({
  id: 'dataprevEmployee.warrantyExecution',
  path: WARRANTY_EXECUTION_PATH,
  title: msg`Execução de garantias`,
  description: msg`Contratos de Crédito do Trabalhador encerrados por término de vínculo (situação 15), com o resultado da execução da garantia do FGTS, o status das rodadas semanais de consulta e de execução na Dataprev, filtros e exportação da planilha. Só leitura.`,
  permission: AUCTION_QUERY,
  place: { icon: 'wallet', section: 'wallet-management' },
  actions: [warrantyExecutionRefresh, warrantyExecutionExport, warrantyExecutionCancelExport, warrantyExecutionFilter, warrantyExecutionPage],
});

/** O que o agente lê da tela (U24, U46): contagem, filtros pelo rótulo e a exportação, no idioma da tela. */
export function warrantyExecutionContext(input: { readonly total: number | undefined; readonly filters: readonly string[]; readonly exporting: boolean }): ScreenContext {
  const total = input.total;
  const count = total === undefined ? t`Carregando.` : plural(total, { one: '# contrato na lista.', other: '# contratos na lista.' });
  const labels = input.filters.map((key) => {
    const label = warrantyExecutionFilters.fieldOf(key)?.label;
    return label ? resolvePatternText(i18n, label) : key;
  });
  const filterList = labels.join(', ');
  const filters = labels.length > 0 ? t`Filtros: ${filterList}.` : t`Sem filtros: últimos 90 dias.`;
  return {
    summary: [t`Execução de garantias.`, count, filters, input.exporting ? t`Exportação da planilha em andamento.` : ''].filter(Boolean).join(' '),
    fields: { total: input.total ?? null, exporting: input.exporting },
    sensitivity: 'restricted',
  };
}
